/**
 * Is a live path actually working, or live in name only?
 * For Media Devoted | Created By Ciro Autiero
 *
 * A path can sit in the Live list doing nothing: switched OFF on the tracker,
 * or left at weight 0 so it receives no traffic. Both occupy a live slot, so
 * both read as "disabled" here — one predicate shared by the badge, the filter
 * and the bulk button, so they can never disagree about what disabled means.
 */

import type { QueueElement } from '@trafficpulse/shared';

/** Third filter axis, Live only. */
export type QueueStatusFilter = 'all' | 'active' | 'disabled';

export const QUEUE_STATUSES: ReadonlyArray<{
  value: QueueStatusFilter;
  label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
];

/** Why a path counts as disabled — the fix differs, so the badge says which. */
export type PathDisabledReason = 'off' | 'no-traffic';

/**
 * Both fields are absent when the flow could not be read, and absent must read
 * as ACTIVE: a failed tracker call must never paint every path as disabled and
 * arm a bulk action that would strip the flow.
 */
export function pathDisabledReason(
  el: QueueElement,
): PathDisabledReason | null {
  if (el.flowActive === false) return 'off';
  if (el.flowWeight === 0) return 'no-traffic';
  return null;
}

/** Live in name only — switched off, or getting no traffic. */
export function isPathDisabled(el: QueueElement): boolean {
  return pathDisabledReason(el) !== null;
}

/** Keep only active / only disabled rows ('all' passes everything). */
export function filterByStatus(
  elements: QueueElement[],
  status: QueueStatusFilter,
): QueueElement[] {
  if (status === 'all') return elements;
  const wantDisabled = status === 'disabled';
  return elements.filter((el) => isPathDisabled(el) === wantDisabled);
}
